function userHasRole(user, role) {
  return (user?.user_roles || []).map((r) => r.role).includes(role);
}

function userHasRoleIn(user, roles) {
  return (user?.user_roles || []).filter((ur) => roles.includes(ur.role)).length > 0;
}

function userIsAdmin(user) {
  return userHasRole(user, "admin");
}

function isGlobalAdmin(user) {
  return userHasRole(user, "admin");
}

function userIsAccountant(user) {
  return userHasRole(user, "accountant");
}

function userIsCarSharingManager(user) {
  return userHasRole(user, "car_sharing_manager");
}

function userIsSame(userA, userB) {
  return userA && userB && userA.id === userB.id;
}

function userIsApproved(user) {
  for (const communityUser of user.accessible_communities) {
    if (communityUser.status === "approved") {
      return true;
    }
  }
  return false;
}

const registeredRequiredFields = ["name", "date_of_birth", "address", "phone"];
function userIsRegistered(user) {
  if (!user) {
    return false;
  }

  for (let i = 0, len = registeredRequiredFields.length; i < len; i += 1) {
    if (!user[registeredRequiredFields[i]]) {
      return false;
    }
  }

  return true;
}

function canAssignFleet(accessingUser) {
  if (!accessingUser) {
    return false;
  }

  // Must be global admin to assign fleet users.
  return userIsAdmin(accessingUser);
}

function canChangeUserPassword(accessingUser, accessedUser) {
  if (!accessingUser) {
    return false;
  }

  // One can change their own password.
  if (userIsSame(accessingUser, accessedUser)) {
    return true;
  }

  // Global admins can too.
  return userIsAdmin(accessingUser);
}

function canEditUser(accessingUser, accessedUser) {
  if (!accessingUser) {
    return false;
  }

  // One can change their own dtails.
  if (userIsSame(accessingUser, accessedUser)) {
    return true;
  }

  return userIsAdmin(accessingUser);
}

function canViewBankDetails(accessingUser) {
  if (!accessingUser) {
    return false;
  }

  return userHasRoleIn(accessingUser, ["admin", "accountant"]);
}

function canEditDriversProfile(accessingUser, accessedUser) {
  if (!accessingUser) {
    return false;
  }

  if (userIsSame(accessingUser, accessedUser)) {
    return true;
  }

  return userIsAdmin(accessingUser);
}

function canSeeDeletedUser(accessingUser) {
  return userIsAdmin(accessingUser);
}

function canManageGlobalRoles(accessingUser) {
  return accessingUser && userIsAdmin(accessingUser);
}

// Can create loan pour un car?
function canBorrowCar(accessingUser) {
  return (
    canBorrowVehicle(accessingUser) &&
    accessingUser?.borrower?.approved_at &&
    !accessingUser?.borrower?.suspended_at
  );
}

function canSeeUserBills(accessingUser) {
  return userIsAdmin(accessingUser);
}

// Communities for which proofs have been submitted or approved
function onboardedCommunities(user) {
  return (user?.accessible_communities || [])
    .filter((cu) => cu.join_method !== "friend")
    .filter((cu) => !["absent", "partial"].includes(cu.proof_state) || cu.status === "approved");
}

function canFillDriverProfile(user) {
  for (const community of onboardedCommunities(user)) {
    if (community.community.shared_loanable_types?.includes("car")) {
      return true;
    }
  }
  return false;
}

function canBorrowVehicle(user) {
  // User can borrow loanables if approved (and not suspended) in any community.
  return (
    user &&
    user.accessible_communities &&
    user.accessible_communities.some((cu) => cu.status === "approved")
  );
}

function canAddLoanables(user) {
  return onboardedCommunities(user).some((cu) => cu.community.members_can_add_loanables);
}

export {
  canAssignFleet,
  canChangeUserPassword,
  canEditDriversProfile,
  canEditUser,
  canBorrowCar,
  canManageGlobalRoles,
  canSeeDeletedUser,
  canSeeUserBills,
  canViewBankDetails,
  isGlobalAdmin,
  userHasRole,
  userIsAdmin,
  userIsAccountant,
  userIsCarSharingManager,
  userIsApproved,
  userIsRegistered,
  userIsSame,
  onboardedCommunities,
  canFillDriverProfile,
  canBorrowVehicle,
  canAddLoanables,
};
